import React from 'react'
import { render as ReactDOMRender } from 'react-dom'
import html2pdf from 'html2pdf.js'
import { ThemeProvider } from 'styled-components'
import { useModalContext } from 'containers/ModalManager/ModalContext'

// Components
import Link from 'components/Link'
import RecoveryCodesPDFTemplate from 'modules/user/components/RecoveryCodesPDFTemplate'

// Modals
import RegenerateRecoveryCodesModal from 'modules/user/modals/RegenerateRecoveryCodesModal'

// Styles
import theme from 'styles/theme'
import {
  Title,
  Description,
  StyledVerificationCode,
  RecoveryCodes,
  RecoveryCode,
  Buttons,
  StyledButton,
} from '../Setup2FAModal.styles'

// Types
interface CodesStepProps {
  onFilled: (value: string) => void
  recoveryCodes: string[]
  error?: string
}

/**
 * CodesStep
 *
 * Renders the third step for the 2FA Setup Modal
 */
export const CodesStep: React.FC<CodesStepProps> = ({
  onFilled,
  recoveryCodes,
  error,
}) => {
  const { showModal, hideModal } = useModalContext()

  const handleGenerateNewCodes = () => {
    hideModal()

    showModal(RegenerateRecoveryCodesModal)
  }

  const handlePrintCodes = async () => {
    const container = document.createElement('div')
    ReactDOMRender(
      <ThemeProvider theme={theme}>
        <RecoveryCodesPDFTemplate recoveryCodes={recoveryCodes} />
      </ThemeProvider>,
      container
    )

    html2pdf(container, {
      filename: 'sketch_recovery_codes.pdf',
    })
  }

  const handleCopyCodes = () => {
    const parsedRecoveryCodes = recoveryCodes.join().replaceAll(',', ' ')

    navigator.clipboard.writeText(parsedRecoveryCodes)
  }

  if (recoveryCodes.length) {
    return (
      <>
        <Title>Recovery Codes</Title>
        <Description>
          Before you finish, you should save these back-up codes in case you
          lose access to the authentication app.
        </Description>
        <RecoveryCodes>
          {recoveryCodes.map((code, index) => (
            <RecoveryCode key={`${code}${index}`}>{code}</RecoveryCode>
          ))}
        </RecoveryCodes>
        <Buttons>
          <StyledButton onClick={handlePrintCodes} smaller>
            Print Codes (PDF)
          </StyledButton>
          <StyledButton onClick={handleCopyCodes} smaller>
            Copy Codes
          </StyledButton>
        </Buttons>
        <Link onClick={handleGenerateNewCodes} isUnderlined external>
          Generate new recovery codes
        </Link>
      </>
    )
  }

  return (
    <>
      <Title>Enter the Verification Code</Title>
      <Description>
        After scanning the QR code you should see a six-digit verification code.
        Enter the code before it expires!
      </Description>
      <StyledVerificationCode onFilled={onFilled} error={error} />
      {/*
        TODO: Add the correct link here when its available
        <Link href="http://www.google.com" isUnderlined external>
          Having trouble with your code?
        </Link>
      */}
    </>
  )
}
